import React from 'react';

// What Q2 has done with a fixture, for the operator only.
//
// Q2 stopped running for every game on 2026-08-28 -- the daemon now summarizes
// the ten closest games per league and waits to be asked for anything else. So
// "does this game have AI analysis?" became a real question with a visible
// answer, and scrolling a slate has to make that answer legible without
// opening every fixture.
//
// `null` is the normal state and reads as "not selected", not as an error. It
// is drawn muted for exactly that reason: on a 68-game NCAAF Saturday most of
// the page is this badge, and anything louder would make the slate look broken.
const STATES = {
  done:       { label: 'Summarised', icon: '✓', color: '#4caf50' },
  queued:     { label: 'Queued',     icon: '○', color: '#FFD700' },
  processing: { label: 'Processing', icon: '◔', color: '#4FC3F7' },
  failed:     { label: 'Failed',     icon: '⚠', color: '#FF6347' },
  none:       { label: 'Not selected', icon: '·', color: 'rgba(255,255,255,0.35)' },
};

// An on-demand league's unsummarized game was never a candidate: nothing selects
// it, it waits to be asked. "Not selected" would imply the selector looked and
// passed, so it says what is actually true.
const ON_REQUEST = { label: 'On request', icon: '·', color: 'rgba(255,255,255,0.35)' };

export const q2StateOf = (fixture, liveState) => {
  // The live poll wins when it has an opinion. The slate payload is cached
  // client-side with an expiry, so its q2_state can be minutes stale -- which
  // is precisely the window an operator is watching after pressing a button.
  if (liveState !== undefined && liveState !== null) return liveState;
  if (fixture?.q2_state) return fixture.q2_state;
  return null;
};

const Q2Badge = ({ state, requested, policy }) => {
  // `requested` is optimistic: the POST has returned but the daemon has not yet
  // claimed the message, so no state has replicated back. Without it the button
  // appears to do nothing for a poll cycle or two.
  const key = requested && !state ? 'queued' : (state || 'none');
  const s = key === 'none' && policy === 'on_demand' ? ON_REQUEST : (STATES[key] || STATES.none);
  const pending = key === 'queued' || key === 'processing';

  return (
    <span
      title={`AI summary: ${s.label}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '0.7rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: s.color,
        border: `1px solid ${s.color}`,
        borderRadius: '12px',
        padding: '1px 9px',
        whiteSpace: 'nowrap',
        // Movement is the point for the two states that are going somewhere --
        // a static "Processing" and a stalled one look identical.
        animation: pending ? 'q2pulse 1.6s ease-in-out infinite' : undefined,
      }}
    >
      <span aria-hidden="true">{s.icon}</span>
      {s.label}
    </span>
  );
};

export default Q2Badge;
